import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import OnboardingCompletionCard from "@/components/OnboardingCompletionCard";
import { GuideQuizDialog } from "@/components/GuideQuiz";
import api, { apiError } from "@/lib/api";
import { BookOpen, Download, FileText, Eye, Check, TrendingUp, GripVertical, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const GUIDES = [
  {
    slug: "guest-portal",
    title: "Guest Portal",
    description: "Magic-link workflow — how guests upload ID, sign the agreement and authorise the deposit.",
    pages: 3,
  },
  {
    slug: "property-docs",
    title: "Property Guide & Rental Agreement",
    description: "Upload bespoke PDFs per property, where each one shows up, and the signed audit trail.",
    pages: 2,
  },
  {
    slug: "cleaning-companies",
    title: "Cleaning Companies",
    description: "Add suppliers, assign them to properties, and understand what the CRM automates after.",
    pages: 2,
  },
  {
    slug: "cleaning-costs",
    title: "Cleaning Costs",
    description: "Record and update what each company charges per property, with payout preview before you save.",
    pages: 2,
  },
  // Session Z74 — "How to" set.
  {
    slug: "how-to-navigate-dashboard",
    title: "How to navigate the Dashboard",
    description: "Tile-by-tile tour of the operations command centre — payout preview, outliers, cron health.",
    pages: 2,
  },
  {
    slug: "how-to-guest-journeys",
    title: "How to use the Guest Journeys tab",
    description: "Every stage of the journey timeline explained — ID, e-sign, deposit, upsells, disputes.",
    pages: 2,
  },
  {
    slug: "how-to-create-message-templates",
    title: "How to create message templates",
    description: "Build reusable email + SMS copy with merge tags, pin them to automations, and test before firing.",
    pages: 2,
  },
  {
    slug: "how-to-create-email-campaign",
    title: "How to create an email campaign",
    description: "Audience filters, preview, scheduling, and reading the per-campaign delivered / opened / clicked stats.",
    pages: 2,
  },
  {
    slug: "how-to-add-upsell-items",
    title: "How to add upsell items",
    description: "Create the extras guests can buy from the Guest Portal — scope per property and keep a clean audit trail.",
    pages: 2,
  },
  {
    slug: "how-to-end-of-month-report",
    title: "How to create an end-of-month financial report",
    description: "Manual month-end payout generation — PDF + CSV + landlord email dispatch with the new ops cost columns.",
    pages: 2,
  },
  {
    slug: "how-to-owner-statements",
    title: "How to understand owner statements",
    description: "FAQ reference for staff answering landlord payout questions — every column, every typical query.",
    pages: 2,
  },
  // Session Z79 — Outlook Calendar OAuth setup.
  {
    slug: "how-to-connect-outlook-calendar",
    title: "How to connect Outlook Calendar",
    description: "Step-by-step Azure app registration for the Microsoft Graph auto-snooze — Teams meetings silence the chime the same way Google Calendar does.",
    pages: 3,
  },
  // Session Z106 — cleaner check-in / check-out.
  {
    slug: "how-to-cleaner-check-in-out",
    title: "How to check in & out of a changeover",
    description: "For cleaners: reveal codes, tap I'm here on arrival, move the card to Done when finished, and what the GPS warning means.",
    pages: 2,
  },
  // Session Z118 — menu navigation guide.
  {
    slug: "how-to-navigate-menu-tabs",
    title: "How to navigate every menu tab",
    description: "A tab-by-tab tour of the left menu: what each page is for, its key buttons and common tasks — tagged Owner / Admin / Cleaner.",
    pages: 6,
  },
  // Session Z80 — bundled handbook.
  {
    slug: "newport-stays-operator-handbook",
    title: "Operator Handbook (full print pack)",
    description: "Every how-to in this grid merged into one indexed PDF — hand it to new hires on day one.",
    pages: 34,
    bundle: true,
  },
];

// Session Z75 — tiny inline SVG sparkline so the Training Guides grid stays
// one tap away from showing which guides are freshest in staff memory. No
// recharts dependency — the chip renders in ~1kb.
function TrendChip({ trend, downloads7d, slug }) {
  if (!Array.isArray(trend) || trend.length === 0) return null;
  const w = 72;
  const h = 20;
  const max = Math.max(1, ...trend.map((p) => p.count || 0));
  const stepX = trend.length > 1 ? w / (trend.length - 1) : w;
  const points = trend.map((p, i) => {
    const x = i * stepX;
    const y = h - ((p.count || 0) / max) * (h - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const stale = downloads7d === 0;
  const first = trend[0]?.date;
  const last = trend[trend.length - 1]?.date;
  const title = `${downloads7d} download${downloads7d === 1 ? "" : "s"} in the last 7 days (${first} → ${last})`;
  return (
    <span
      data-testid={`training-guide-trend-${slug}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 border ${
        stale
          ? "bg-slate-50 border-slate-200 text-slate-500"
          : "bg-indigo-50 border-indigo-100 text-indigo-700"
      }`}
      title={title}
    >
      <TrendingUp className="w-3 h-3" />
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0">
        <polyline
          points={points}
          fill="none"
          stroke={stale ? "#94a3b8" : "#4f46e5"}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <span className="tabular-nums">
        {downloads7d}/7d
      </span>
    </span>
  );
}

export default function TrainingGuides() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [myReads, setMyReads] = useState({});
  const [togglingRead, setTogglingRead] = useState(null);
  const [openRoster, setOpenRoster] = useState(null);
  const isTeamLead = user?.role === "admin" || user?.role === "owner";

  const loadAnalytics = async () => {
    try {
      const { data } = await api.get("/training/analytics");
      setAnalytics(data);
    } catch { setAnalytics(null); }
  };
  const loadReads = async () => {
    try {
      const { data } = await api.get("/training/my-reads");
      setMyReads(data?.reads || {});
    } catch { setMyReads({}); }
  };

  useEffect(() => {
    loadAnalytics();
    loadReads();
  }, []);

  // Session Z81 — Operator Handbook reorder dialog.
  const [reorderOpen, setReorderOpen] = useState(false);
  const [downloadingHandbook, setDownloadingHandbook] = useState(false);
  const [hasCustomOrder, setHasCustomOrder] = useState(false);

  useEffect(() => {
    api.get("/settings/handbook-order")
      .then((r) => setHasCustomOrder((r.data?.order || []).length > 0))
      .catch(() => {});
  }, [reorderOpen]);

  const downloadHandbook = async () => {
    setDownloadingHandbook(true);
    try {
      const endpoint = hasCustomOrder
        ? "/training/operator-handbook/mine.pdf"
        : "/training/newport-stays-operator-handbook.pdf";
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "newport-stays-operator-handbook.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      api.post("/training/newport-stays-operator-handbook/log-download").catch(() => {}).finally(loadAnalytics);
    } catch {
      toast.error("Could not download the Operator Handbook");
    } finally {
      setDownloadingHandbook(false);
    }
  };

  const download = async (slug, title) => {
    try {
      const res = await api.get(`/training/${slug}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `newport-stays-${slug}-training.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      api.post(`/training/${slug}/log-download`).catch(() => {}).finally(loadAnalytics);
    } catch { toast.error(`Could not download the ${title} guide`); }
  };

  const [quizSlug, setQuizSlug] = useState(null);
  const [QUIZ_SLUGS, setQuizSlugs] = useState(new Set());
  useEffect(() => { api.get("/training/quizzes").then((r) => setQuizSlugs(new Set(r.data.slugs))).catch(() => {}); }, []);
  const toggleRead = async (slug) => {
    if (!myReads[slug] && user?.role === "cleaner" && QUIZ_SLUGS.has(slug)) { setQuizSlug(slug); return; }
    setTogglingRead(slug);
    try {
      if (myReads[slug]) {
        await api.delete(`/training/${slug}/mark-read`);
        setMyReads((r) => { const n = { ...r }; delete n[slug]; return n; });
        toast.success("Unmarked");
      } else {
        const { data } = await api.post(`/training/${slug}/mark-read`);
        setMyReads((r) => ({ ...r, [slug]: data.read_at }));
        toast.success("Marked as read");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTogglingRead(null);
    }
  };

  // Session Z57 — streak + staleness. We ask teams to re-read every 6 months
  // so compliance stays fresh; anything older than that gets an amber nudge.
  const readStreak = (iso) => {
    if (!iso) return null;
    const read = new Date(iso).getTime();
    const diffMs = Date.now() - read;
    const days = Math.round(diffMs / (24 * 60 * 60 * 1000));
    const stale = days > 180;
    let label;
    if (days < 1) label = "read today";
    else if (days === 1) label = "read yesterday";
    else if (days < 30) label = `read ${days} days ago`;
    else if (days < 365) {
      const months = Math.round(days / 30);
      label = `read ${months} month${months === 1 ? "" : "s"} ago`;
    } else {
      const years = Math.round(days / 365);
      label = `read ${years} year${years === 1 ? "" : "s"} ago`;
    }
    return { label, stale, days };
  };

  const getStats = (slug) =>
    (analytics?.guides || []).find((g) => g.slug === slug) || null;

  return (
    <Layout
      title="Training guides"
      subtitle="Printable PDFs for onboarding staff — tap Mark as read when you're done"
    >
      <div className="grid md:grid-cols-2 gap-5" data-testid="training-guides-grid">
        {GUIDES.map((g) => {
          const stats = getStats(g.slug);
          const readAt = myReads[g.slug];
          const isRead = !!readAt;
          return (
            <div
              key={g.slug}
              data-testid={`training-guide-${g.slug}`}
              className={`ns-card p-6 flex flex-col ${isRead ? "ring-1 ring-emerald-200" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="ns-label text-teal-700">PDF · {g.pages} pages</div>
                  <div className="font-serif text-2xl mt-1 text-slate-900">{g.title}</div>
                  <div className="text-sm text-slate-500 mt-2 leading-relaxed">
                    {g.description}
                  </div>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  isRead ? "bg-emerald-50 text-emerald-700" : "bg-teal-50 text-teal-700"
                }`}>
                  {isRead ? <Check className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                </div>
              </div>
              {stats && (stats.total_downloads > 0 || isTeamLead) && (
                <div
                  data-testid={`training-guide-stats-${g.slug}`}
                  className="mt-4 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap"
                >
                  <span className="inline-flex items-center gap-1 rounded-full bg-stone-50 border border-slate-200 px-2.5 py-1">
                    <Eye className="w-3 h-3" />
                    {stats.total_downloads} download{stats.total_downloads === 1 ? "" : "s"}
                  </span>
                  {isTeamLead && stats.unique_users > 0 && (
                    <button
                      type="button"
                      data-testid={`training-guide-roster-${g.slug}`}
                      onClick={() => setOpenRoster(openRoster === g.slug ? null : g.slug)}
                      className="underline hover:text-teal-700"
                    >
                      by {stats.unique_users} staff member{stats.unique_users === 1 ? "" : "s"}
                    </button>
                  )}
                  {stats.last_downloaded_at && (
                    <span className="text-slate-400">
                      · last {new Date(stats.last_downloaded_at).toLocaleDateString("en-GB")}
                    </span>
                  )}
                  {isTeamLead && stats.trend_7d && (
                    <TrendChip
                      trend={stats.trend_7d}
                      downloads7d={stats.downloads_7d || 0}
                      slug={g.slug}
                    />
                  )}
                </div>
              )}
              {isTeamLead && openRoster === g.slug && stats && stats.roster.length > 0 && (
                <ul
                  data-testid={`training-guide-roster-list-${g.slug}`}
                  className="mt-3 bg-stone-50/60 border border-slate-100 rounded-lg divide-y divide-slate-100 text-xs"
                >
                  {stats.roster.map((r) => (
                    <li key={r.user_id || r.user_email} className="px-3 py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-slate-700 truncate">
                          {r.user_name || r.user_email || "unknown"}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {r.user_role || "staff"} · {r.user_email}
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-500 shrink-0 text-right">
                        <div>{r.downloads}×</div>
                        <div className="text-slate-400">
                          {new Date(r.last_downloaded_at).toLocaleDateString("en-GB")}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                {g.bundle ? (
                  <>
                    <button
                      data-testid={`training-guide-download-${g.slug}`}
                      onClick={downloadHandbook}
                      disabled={downloadingHandbook}
                      className="inline-flex items-center gap-2 rounded-full ns-btn-primary px-5 py-2 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      {downloadingHandbook ? "Preparing…" : (hasCustomOrder ? "Download my handbook" : "Download handbook")}
                    </button>
                    <button
                      type="button"
                      data-testid="training-guide-reorder-handbook"
                      onClick={() => setReorderOpen(true)}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white hover:border-teal-600 hover:text-teal-700 text-slate-600 px-4 py-2 text-sm"
                    >
                      <GripVertical className="w-4 h-4" />
                      Reorder chapters
                    </button>
                    {hasCustomOrder && (
                      <span className="text-[11px] rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-3 py-1">
                        Custom order active
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      data-testid={`training-guide-download-${g.slug}`}
                      onClick={() => download(g.slug, g.title)}
                      className="inline-flex items-center gap-2 rounded-full ns-btn-primary px-5 py-2 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </button>
                    <a
                      data-testid={`training-guide-preview-${g.slug}`}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/training/${g.slug}.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white hover:border-teal-600 hover:text-teal-700 text-slate-600 px-4 py-2 text-sm"
                    >
                      <BookOpen className="w-4 h-4" />
                      Preview in browser
                    </a>
                    <button
                      data-testid={`training-guide-mark-read-${g.slug}`}
                      type="button"
                      onClick={() => toggleRead(g.slug)}
                      disabled={togglingRead === g.slug}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition ${
                        isRead
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                          : "bg-white border-slate-200 text-slate-600 hover:border-emerald-600 hover:text-emerald-700"
                      }`}
                      title={isRead ? `Marked read on ${new Date(readAt).toLocaleDateString("en-GB")}` : "Confirm you've read this guide"}
                    >
                      <Check className="w-4 h-4" />
                      {isRead ? "Marked as read" : (user?.role === "cleaner" && QUIZ_SLUGS.has(g.slug) ? "Take quiz to mark read" : "Mark as read")}
                    </button>
                  </>
                )}
              </div>
              {isRead && (() => {
                const streak = readStreak(readAt);
                if (!streak) return null;
                return (
                  <div
                    data-testid={`training-guide-streak-${g.slug}`}
                    className={`mt-3 inline-flex items-center gap-2 text-[11px] rounded-full px-3 py-1 border ${
                      streak.stale
                        ? "bg-amber-50 border-amber-200 text-amber-700"
                        : "bg-emerald-50 border-emerald-100 text-emerald-700"
                    }`}
                    title={streak.stale ? "Over 6 months old — give it a re-read" : "Fresh — great work"}
                  >
                    <Check className="w-3 h-3" />
                    ✓ {streak.label}
                    {streak.stale && (
                      <span className="text-amber-700/80">· time to re-read</span>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      <div className="mt-6 ns-card p-5 text-xs text-slate-500">
        Mark-as-read status powers the Dashboard's <b>Training guide completion</b> tile.
        We ask teams to re-read every six months so compliance stays fresh — tiles
        older than that get an amber "time to re-read" chip. Need another guide? Drop
        a request in the admin inbox — new PDFs are generated with ReportLab and dropped
        into <code>/app/backend/static/</code>, then surfaced here automatically.
      </div>

      <ChapterReorderDialog
        open={reorderOpen}
        onClose={() => setReorderOpen(false)}
      />
      <div className="mt-8"><OnboardingCompletionCard /></div>
      {quizSlug && (
        <GuideQuizDialog slug={quizSlug} onClose={() => setQuizSlug(null)}
          onPassed={(at) => setMyReads((r) => ({ ...r, [quizSlug]: at }))} />
      )}
    </Layout>
  );
}


// Session Z81 — drag-and-drop handbook chapter reorder (native HTML5 DnD so
// we don't add a dep). Owners can arrange chapters into their preferred
// induction sequence; the backend builds + caches the custom PDF on download.
function ChapterReorderDialog({ open, onClose }) {
  const [chapters, setChapters] = useState([]);
  const [defaultOrder, setDefaultOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    api.get("/settings/handbook-order")
      .then((r) => {
        const catalog = r.data?.catalog || [];
        const saved = r.data?.order || [];
        setDefaultOrder(r.data?.default_order || catalog.map((c) => c.slug));
        if (saved.length === 0) {
          setChapters(catalog);
        } else {
          const titleBySlug = Object.fromEntries(catalog.map((c) => [c.slug, c.title]));
          setChapters(saved
            .filter((s) => titleBySlug[s])
            .map((s) => ({ slug: s, title: titleBySlug[s] })));
        }
      })
      .catch(() => toast.error("Could not load current order"))
      .finally(() => setLoaded(true));
  }, [open]);

  const move = (from, to) => {
    if (from === to || from === null || to === null) return;
    setChapters((prev) => {
      const next = [...prev];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/handbook-order", { order: chapters.map((c) => c.slug) });
      toast.success("Chapter order saved — your handbook will rebuild on next download");
      onClose();
    } catch (err) {
      toast.error(apiError(err, "Could not save chapter order"));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.put("/settings/handbook-order", { order: [] });
      toast.success("Reset to the default induction order");
      onClose();
    } catch (err) {
      toast.error(apiError(err, "Could not reset order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="handbook-reorder-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Reorder handbook chapters</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          Drag chapters into your preferred induction order. Your choice saves against your
          account only — teammates keep seeing the default order unless they personalise too.
        </p>
        {!loaded ? (
          <div className="text-sm text-slate-400 py-6 text-center">Loading current order…</div>
        ) : (
          <ul className="mt-2 space-y-2 max-h-[52vh] overflow-y-auto pr-1" data-testid="handbook-reorder-list">
            {chapters.map((c, i) => (
              <li
                key={c.slug}
                data-testid={`handbook-reorder-row-${c.slug}`}
                draggable
                onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
                onDragLeave={() => setOverIdx((n) => (n === i ? null : n))}
                onDrop={(e) => { e.preventDefault(); move(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 bg-white cursor-grab active:cursor-grabbing transition ${
                  overIdx === i && dragIdx !== i
                    ? "border-teal-500 bg-teal-50/60"
                    : "border-slate-200"
                }`}
              >
                <GripVertical className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-700 truncate">
                    <span className="text-slate-400 mr-2">{i + 1}.</span>
                    {c.title}
                  </div>
                  <div className="text-[10px] text-slate-400 truncate">{c.slug}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter className="gap-2">
          <Button
            data-testid="handbook-reorder-reset"
            variant="outline"
            onClick={reset}
            disabled={busy || !loaded}
            className="rounded-full"
          >
            Reset to default
          </Button>
          <Button
            data-testid="handbook-reorder-save"
            onClick={save}
            disabled={busy || !loaded || chapters.length === 0}
            className="rounded-full ns-btn-primary"
          >
            {busy ? "Saving…" : "Save order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
